import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { Button, toast } from '@ludolio/shared-ui';
import { FullPageMessage, FullPageSpinner } from '@/components/FullPageMessage';
import { LabsUnavailable } from '@/components/LabsUnavailable';
import { Layout } from '@/components/Layout';
import { RemixDialog } from '@/components/RemixDialog';
import { RequireAuth } from '@/components/RequireAuth';
import { getErrorMessage, getErrorStatus, labsApi } from '@/lib/api';
import { LabsContextValue } from '@/lib/labs-context';
import type { LabsContext } from '@/lib/types';

export const Route = createFileRoute('/remix/$slug')({
  component: () => (
    <RequireAuth>
      <RemixGate />
    </RequireAuth>
  ),
});

function RemixGate() {
  const { slug } = Route.useParams();
  const [context, setContext] = useState<LabsContext | 'unavailable' | 'loading' | { error: string }>('loading');

  const load = useCallback(() => {
    setContext('loading');
    labsApi
      .context()
      .then(setContext)
      .catch((error) => {
        if (getErrorStatus(error) === 404) setContext('unavailable');
        else setContext({ error: getErrorMessage(error) });
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (context === 'loading') return <FullPageSpinner />;
  if (context === 'unavailable') return <LabsUnavailable />;
  if ('error' in context) {
    return (
      <FullPageMessage title={context.error}>
        <Button type="button" onClick={load}>Try again</Button>
      </FullPageMessage>
    );
  }

  return (
    <LabsContextValue.Provider value={context}>
      <Layout>
        <RemixPage slug={slug} orgSlug={context.organization.slug} />
      </Layout>
    </LabsContextValue.Provider>
  );
}

function RemixPage({ slug, orgSlug }: { slug: string; orgSlug: string }) {
  const navigate = useNavigate();

  const leave = () => {
    void navigate({ to: '/$orgSlug/projects', params: { orgSlug } });
  };

  return (
    <RemixDialog
      slug={slug}
      open
      onOpenChange={(open) => {
        if (!open) leave();
      }}
      onCreated={(project) => {
        toast.success('Remix created. Download the original source from Lineage.');
        void navigate({
          to: '/$orgSlug/projects/$projectId',
          params: { orgSlug, projectId: project.project.id },
          hash: 'lineage',
        });
      }}
    />
  );
}
